import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { InfoBubbleStyled, InfoBubbleTextContainerStyled, InfoBubbleTextStyled, InfoIconStyled } from '../styles/Main';
import iconMap from '../data/iconMap';
import { IconContext } from "react-icons";
import { IoHelpCircle } from 'react-icons/io5';

// Original full-size InfoBubble
function InfoBubble({info}) {
    const [displayed, setDisplayed] = useState(false);

    return (
        <InfoBubbleStyled>
            <IconContext.Provider value={{ style: { fontSize: '24px' } }}>
                <InfoIconStyled onClick={() => setDisplayed(!displayed)}>{displayed?iconMap['X']:iconMap['Info']}</InfoIconStyled>
            </IconContext.Provider>
            <InfoBubbleTextContainerStyled $displayed={displayed}>
                <InfoBubbleTextStyled>{info}</InfoBubbleTextStyled>
            </InfoBubbleTextContainerStyled>
        </InfoBubbleStyled>
    );
}

// Inline tooltip for form field labels
const TooltipWrapper = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: help;
`;

const TooltipIcon = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    transition: opacity 0.2s ease, color 0.2s ease, transform 0.2s ease;
    
    /* Light variant for dark backgrounds (like table headers) */
    ${({ $light, theme }) => $light ? css`
        color: rgba(255, 255, 255, 0.85);
        background: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        padding: 1px;
        
        &:hover {
            color: #fff;
            background: rgba(255, 255, 255, 0.35);
            transform: scale(1.1);
        }
    ` : css`
        color: ${theme.colors?.subTextColor || '#666'};
        opacity: 0.7;
        
        &:hover {
            opacity: 1;
            color: ${theme.colors?.activeColor || '#2563eb'};
        }
    `}
`;

const TooltipContent = styled.div`
    position: fixed;
    background: #ffffff;
    color: #333;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.4;
    white-space: normal;
    min-width: 200px;
    max-width: 280px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 10000;
    pointer-events: none;
    opacity: ${({ $visible }) => $visible ? 1 : 0};
    visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
    transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const TooltipArrow = styled.div`
    position: fixed;
    z-index: 10000;
    pointer-events: none;
    opacity: ${({ $visible }) => $visible ? 1 : 0};
    visibility: ${({ $visible }) => $visible ? 'visible' : 'hidden'};
    transition: opacity 0.2s ease, visibility 0.2s ease;
    
    &::after {
        content: '';
        position: absolute;
        border: 6px solid transparent;
        border-top-color: #ffffff;
    }
`;

export function FieldTooltip({ text, light = false }) {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, arrowLeft: 0, arrowTop: 0 });
    const iconRef = useRef(null);

    useEffect(() => {
        if (visible && iconRef.current) {
            const rect = iconRef.current.getBoundingClientRect();
            const tooltipWidth = 240; // approximate width
            
            // Calculate tooltip position (above the icon, centered)
            let left = rect.left + rect.width / 2 - tooltipWidth / 2;
            const top = rect.top - 8; // 8px gap above icon
            
            // Keep tooltip within viewport
            const padding = 10;
            if (left < padding) left = padding;
            if (left + tooltipWidth > window.innerWidth - padding) {
                left = window.innerWidth - tooltipWidth - padding;
            }
            
            setPosition({
                top,
                left,
                arrowLeft: rect.left + rect.width / 2 - 6, // center arrow on icon
                arrowTop: rect.top - 8
            });
        }
    }, [visible]);

    if (!text) return null;

    const tooltipContent = (
        <>
            <TooltipContent 
                $visible={visible}
                style={{ 
                    top: `${position.top}px`, 
                    left: `${position.left}px`,
                    transform: 'translateY(-100%)'
                }}
            >
                {text}
            </TooltipContent>
            <TooltipArrow
                $visible={visible}
                style={{
                    top: `${position.arrowTop}px`,
                    left: `${position.arrowLeft}px`,
                }}
            >
                <span style={{
                    position: 'absolute',
                    top: '-6px',
                    left: '0',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #ffffff',
                }} />
            </TooltipArrow>
        </>
    );

    return (
        <TooltipWrapper
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onClick={() => setVisible(!visible)}
        >
            <TooltipIcon ref={iconRef} $light={light}>
                <IoHelpCircle size={16} />
            </TooltipIcon>
            {createPortal(tooltipContent, document.body)}
        </TooltipWrapper>
    );
}

export default InfoBubble;